import React, { useState, useEffect, useMemo } from 'react';
import {
  Firestore,
  collection,
  query,
  where,
  getDocs,
  orderBy,
} from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../../hooks/AuthProvider';
import LoadingPage from '../../LoadingPage';
import ControlPanel from '../ControlPanel/ControlPanel';
import CreateAppView from '../CreateAppView/CreateAppView';
import './AppsPage.css';

interface Organization {
  id: string;
  orgId: string;
  orgName: string;
  country: string;
  countryCode: string;
  currencyCode: string;
  region: string;
  availableTimezones: string[];
  currencySymbol: string;
  stripeAccount: string;
  _metadata: {
    createdAt: { _seconds: number; _nanoseconds: number };
    updatedAt: { _seconds: number; _nanoseconds: number };
  };
}

interface App {
  id: string;
  name: string;
  description?: string;
  organizationId: string;
  createdBy: string;
  createdAt: any;
  status?: string;
  type?: string;
  enabled?: boolean;
  region?: string;
  appIcon?: string;
  decommissioned?: boolean;
}

interface AppsPageProps {
  db: Firestore;
  functions: Functions;
  organizationId: string;
  setOrganizationId: (id: string) => void;
  organizations: Record<string, Organization>;
  onOrganizationChange: (orgId: string) => void;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  setLoading: (loading: boolean) => void;
}

const SELECTED_ORG_KEY = 'dineplatform_selected_organization';
const PAGE_SIZES = [10, 25, 50];

const statusClass = (status?: string) => {
  if (!status || status === 'unknown') return 'unknown';
  if (status === 'active' || status === 'completed') return 'active';
  if (status === 'inactive' || status === 'disabled') return 'inactive';
  if (status === 'deploying' || status === 'pending' || status === 'in-progress') return 'deploying';
  if (status === 'error' || status === 'failed') return 'error';
  return 'unknown';
};

const AppsPage: React.FC<AppsPageProps> = ({
  db,
  functions,
  organizationId,
  setOrganizationId,
  organizations,
  onOrganizationChange,
  onError,
  onSuccess,
  setLoading,
}) => {
  const { currentUser } = useAuth();

  const [apps, setApps] = useState<App[]>([]);
  const [localLoading, setLocalLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState<App | null>(null);
  const [showCreateApp, setShowCreateApp] = useState(false);
  const [openMenuAppId, setOpenMenuAppId] = useState<string | null>(null);

  // Table state
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Persist selected org
  useEffect(() => {
    if (organizationId && organizations[organizationId]) {
      localStorage.setItem(SELECTED_ORG_KEY, organizationId);
    }
  }, [organizationId, organizations]);

  const loadApps = async () => {
    if (!currentUser?.email || !organizationId) { setLocalLoading(false); return; }
    setLocalLoading(true);
    setLoading(true);
    try {
      const q = query(
        collection(db, 'apps'),
        where('organizationId', '==', organizationId),
        where('createdBy', '==', currentUser.email),
        orderBy('createdAt', 'desc'),
      );
      const snap = await getDocs(q);
      const data: App[] = [];
      snap.forEach((doc) => {
        const d = doc.data();
        if (d.decommissioned !== true) {
          data.push({
            id: doc.id,
            name: d.name || 'Untitled App',
            description: d.description || '',
            organizationId: d.organizationId || '',
            createdBy: d.createdBy || '',
            createdAt: d.createdAt,
            status: d.status || 'unknown',
            type: d.type || 'single-store',
            enabled: d.enabled ?? true,
            region: d.region || '',
            appIcon: d.appIcon || '',
            decommissioned: d.decommissioned || false,
          });
        }
      });
      setApps(data);
    } catch (err) {
      console.error('Error loading apps:', err);
      onError('Failed to load apps. Please try again.');
    } finally {
      setLocalLoading(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser?.email && organizationId) loadApps();
    else { setLocalLoading(false); setLoading(false); }
  }, [currentUser?.email, organizationId]);

  // Reset page on search/org change
  useEffect(() => { setPage(1); }, [search, organizationId, perPage]);

  // Close menu on outside click
  useEffect(() => {
    const handler = () => { if (openMenuAppId) setOpenMenuAppId(null); };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openMenuAppId]);

  // Filtered + paginated data
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return apps;
    return apps.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        a.id.toLowerCase().includes(q) ||
        (a.type || '').toLowerCase().includes(q),
    );
  }, [apps, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(page, totalPages);
  const pageApps = filtered.slice((safePage - 1) * perPage, safePage * perPage);

  const pageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (safePage > 3) pages.push('ellipsis');
      for (let i = Math.max(2, safePage - 1); i <= Math.min(totalPages - 1, safePage + 1); i++) pages.push(i);
      if (safePage < totalPages - 2) pages.push('ellipsis');
      pages.push(totalPages);
    }
    return pages;
  };

  const handleDeleteApp = async (e: React.MouseEvent, app: App) => {
    e.stopPropagation();
    setOpenMenuAppId(null);
    const input = window.prompt(
      `⚠️ This cannot be undone!\n\nType the app ID to confirm decommissioning "${app.name}":\n\n${app.id}`,
    );
    if (!input || input.trim() !== app.id) {
      if (input !== null) onError('App ID does not match. Cancelled.');
      return;
    }
    setLoading(true);
    try {
      await httpsCallable(functions, 'decommissionApp')({ appId: app.id });
      onSuccess(`Successfully decommissioned ${app.name}`);
      await loadApps();
    } catch (err: any) {
      onError(err.message || 'Failed to decommission app.');
    } finally {
      setLoading(false);
    }
  };

  // ── Sub-views ──────────────────────────────────────────────────

  if (localLoading) return <LoadingPage message="Loading your apps..." />;

  if (!currentUser?.email) {
    return (
      <div className="apps-auth-required">
        <h2>Authentication Required</h2>
        <p>Please log in to view your apps.</p>
      </div>
    );
  }

  if (showCreateApp) {
    return (
      <CreateAppView
        functions={functions}
        organizationId={organizationId}
        organizations={organizations}
        onError={onError}
        onSuccess={onSuccess}
        setLoading={setLoading}
        onBack={() => setShowCreateApp(false)}
        onAppCreated={() => { setShowCreateApp(false); loadApps(); }}
      />
    );
  }

  if (selectedApp) {
    return (
      <ControlPanel
        db={db}
        functions={functions}
        appData={selectedApp}
        organizationId={organizationId}
        onBack={() => setSelectedApp(null)}
        onError={onError}
        onSuccess={onSuccess}
        setLoading={setLoading}
      />
    );
  }

  const selectedOrg = organizations[organizationId];
  const start = (safePage - 1) * perPage + 1;
  const end = Math.min(safePage * perPage, filtered.length);

  return (
    <div className="apps-page">

      {/* ── Header ──────────────────────────────────────────── */}
      <div className="apps-header">
        <div className="apps-header-inner">
          <div className="apps-header-left">
            <h1>My Sites</h1>
            {selectedOrg && (
              <div className="organization-info">
                <span className="organization-name">{selectedOrg.orgName}</span>
                <span className="organization-region">{selectedOrg.region}</span>
              </div>
            )}
          </div>

          <div className="apps-header-right">
            <div className="organization-selector">
              <label htmlFor="org-select">Organization</label>
              <select
                id="org-select"
                value={organizationId}
                onChange={(e) => onOrganizationChange(e.target.value)}
                className="org-dropdown"
              >
                {Object.entries(organizations).map(([orgId, org]) => (
                  <option key={orgId} value={orgId}>
                    {org.orgName} ({org.region})
                  </option>
                ))}
              </select>
            </div>

            <button className="apps-add-btn" onClick={() => setShowCreateApp(true)}>
              <span className="apps-add-btn-icon">+</span>
              Add Site
            </button>
          </div>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────── */}
      <div className="apps-content">
        <div className="apps-content-inner">

          {apps.length === 0 ? (
            <div className="apps-empty-state">
              <div className="apps-empty-state-icon">🌐</div>
              <h3>Create Your First Site</h3>
              <p>Get started by creating your first ordering site in {selectedOrg?.orgName || 'this organization'}.</p>
              <button className="apps-empty-state-btn" onClick={() => setShowCreateApp(true)}>
                <span>+</span> Create Your First Site
              </button>
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div className="apps-toolbar">
                <span className="apps-count">{filtered.length} site{filtered.length !== 1 ? 's' : ''}</span>
                <div className="apps-search">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input
                    type="text"
                    placeholder="Search sites…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>

              {/* Table */}
              <div className="apps-table-wrap">
                <table className="apps-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th className="col-id">App ID</th>
                      <th className="col-type">Type</th>
                      <th className="col-created-by">Created By</th>  {/* ← add */}
                      <th className="th-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageApps.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'center', padding: '40px 16px', color: '#6b7280' }}>
                          No sites match your search.
                        </td>
                      </tr>
                    ) : pageApps.map((app) => (
                      <tr
                        key={app.id}
                        className="apps-table-row"
                        onClick={() => setSelectedApp(app)}
                      >
                        <td>
                          <div className="apps-td-name">
                            <span className="apps-name">{app.name}</span>
                          </div>
                        </td>
                        <td className="col-id">
                          <span className="apps-id">{app.id}</span>
                        </td>
                        <td className="col-type">
                          <span className="apps-type-badge">{app.type || 'unknown'}</span>
                        </td>
                        <td className="col-created-by">          {/* ← add */}
                          <span className="apps-created-by">{app.createdBy}</span>
                        </td>
                        <td className="apps-td-actions" onClick={(e) => e.stopPropagation()}>
                          <div className="apps-td-actions-inner">
                            <div className="app-menu-wrap">
                              <button
                                className="app-card-menu-btn"
                                onClick={(e) => { e.stopPropagation(); setOpenMenuAppId(openMenuAppId === app.id ? null : app.id); }}
                                aria-label="Options"
                              >
                                ⋮
                              </button>
                              {openMenuAppId === app.id && (
                                <div className="app-card-menu">
                                  <button
                                    className="app-card-menu-item app-card-menu-item-delete"
                                    onClick={(e) => handleDeleteApp(e, app)}
                                  >
                                    <span className="app-card-menu-icon">🗑️</span>
                                    Decommission
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {true ? (
                <div className="apps-pagination">
                  <span className="apps-pagination-info">
                    {filtered.length > 0 ? `${start}–${end} of ${filtered.length}` : '0 results'}
                  </span>

                  <div className="apps-pagination-controls">
                    <button
                      className="apps-page-btn"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={safePage === 1}
                      aria-label="Previous"
                    >
                      ‹
                    </button>

                    {pageNumbers().map((p, i) =>
                      p === 'ellipsis' ? (
                        <span key={`e${i}`} className="apps-page-ellipsis">…</span>
                      ) : (
                        <button
                          key={p}
                          className={`apps-page-btn ${safePage === p ? 'active' : ''}`}
                          onClick={() => setPage(p as number)}
                        >
                          {p}
                        </button>
                      )
                    )}

                    <button
                      className="apps-page-btn"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={safePage === totalPages}
                      aria-label="Next"
                    >
                      ›
                    </button>
                  </div>

                  <div className="apps-per-page">
                    <span>Per page</span>
                    <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}>
                      {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              ) : null}
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default AppsPage;